import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cidadeService } from '@/services/api';
import { Cidade, PaginationParams, PaginationResponse } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const CidadesPage: React.FC = () => {
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
  });
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateCidades } = useQueryInvalidation();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  const [searchText, setSearchText] = useState<string>("");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const { data: cidadesData, isLoading, error } = useQuery<PaginationResponse<Cidade>>({
    queryKey: ['cidades', filters],
    queryFn: () => cidadeService.list(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar cidades');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (cidadesData) {
      setLoadError(null);
    }
  }, [cidadesData]);

  const deleteCidadeMutation = useMutation({
    mutationFn: cidadeService.delete,
    onSuccess: (_data, deletedId) => {
      invalidateCidades(deletedId);
      handleSuccess('Cidade excluída com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir cidade');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Cidade',
      'Tem certeza que deseja excluir esta cidade? Esta ação não pode ser desfeita.',
      async () => {
        await deleteCidadeMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Cidades</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie as cidades cadastradas no sistema
          </p>
        </div>
        <Link to="/cidades/nova" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto">
            <PlusIcon className="h-4 w-4 mr-2" />
            Nova Cidade
          </Button>
        </Link>
      </div>

      {/* Cidades List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Cidades</CardTitle>
              <CardDescription>
                {cidadesData?.pagination.total || 0} cidades encontradas
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por nome ou UF"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : cidadesData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma cidade encontrada</p>
              <Link to="/cidades/nova">
                <Button className="mt-4">Cadastrar primeira cidade</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cidadesData?.data.map((cidade: Cidade) => (
                <div
                  key={cidade.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 p-2 bg-blue-100 rounded-md">
                        <MapPinIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{cidade.nome}</h3>
                        <p className="text-sm text-gray-600">UF: {cidade.uf}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2 justify-end sm:justify-start flex-shrink-0">
                      <Link to={`/cidades/${cidade.id}/editar`}>
                        <Button variant="outline" size="sm" className="text-yellow-600 hover:text-yellow-700 hover:border-yellow-300" title="Editar">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(cidade.id)}
                        disabled={deleteCidadeMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        title="Excluir"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {cidadesData && cidadesData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!cidadesData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {cidadesData.pagination.page} de {cidadesData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!cidadesData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteCidadeMutation.isPending}
      />
    </div>
  );
};
